import { COMPUTE_CLASSES, GPU_COMPUTE_CLASSES, isComputeClass, isGpuComputeClass } from "./compute-class.js";
import { compareScaledK8sQuantities } from "./fixture-resources.js";
//#region src/fixture-placement-rules.ts
/**
* compute-classes D3 — `nodeType` is REMOVED from the contract for new publishes.
*
* ⛔ WHY AN ERROR AND NOT A WARNING. `nodeType` names a raw cloud SKU, and because the demand-pool
* architecture is 1:1 machine-type→pool, an app declaring one was choosing a NODE POOL — the exact
* coupling classes remove. It was ALSO the platform's only wired placement field, so leaving it
* accepted means two vocabularies for one decision with no way to tell an edited manifest from an
* unedited one BY SHAPE. Verified live 2026-08-08: ZERO fixture/worker manifests declare one apart
* from `gpu-llm`, which migrates in this same PR (D11) — so the blast radius of the error is one
* manifest we control.
*
* ⛔ THE FIELD IS NOT DELETED from the type or from `fixture.schema.json`. That is the retention
* rule: an already-published manifest that declares it must still validate at operator reconcile,
* because a removed field makes that fixture Ajv-fail PER-FIXTURE every cycle — `fixture-deployer`
* catches, records `{action:'failed'}` and `continue`s, so ONE fixture silently stops deploying
* while its co-tenants are fine. Refused at the two PUBLISH doors, tolerated at reconcile.
*/
function nodeTypeRemovedIssue(path, label, nt) {
	return {
		severity: "error",
		path,
		rule: "node-type-removed",
		message: `${label} declares 'nodeType' ('${safeRuleLabel(nt)}'), which is REMOVED from the contract. A raw cloud machine type couples the app to one cloud, and because each machine type has its own node pool it also chose a pool. Declare 'class' instead (one of: ${COMPUTE_CLASSES.join(", ")}) and put what you actually need in 'resources' — the platform resolves the smallest machine type in that class satisfying your vector, and picks the generation itself.`
	};
}
/**
* compute-classes D3 / D5 — `machineType` (the R-9 opaque catalog SLUG) is REMOVED for new
* publishes.
*
* ⛔ THIS FIELD WAS NEVER WIRED, AND THAT IS WHY IT IS AN ERROR RATHER THAN A DEPRECATION. It was
* the platform's own RECOMMENDED field — publish validation actively pushed authors to delete the
* working `nodeType` and keep this one — and it had ZERO readers: no slug server, no slug table, no
* envelope producer or consumer, no operator resolution. A fixture declaring it rode the
* default-app pool while the manifest said otherwise. A validated-and-ignored field is worse than a
* refused one, and the slug design it belonged to is superseded by classes (D5): there is no slug
* server and there will not be one.
*
* Retained in the type and the schema for the same reconcile back-compat reason as
* {@link nodeTypeRemovedIssue}.
*/
function machineTypeNotSupportedIssue(path, label, mt) {
	return {
		severity: "error",
		path,
		rule: "machine-type-not-supported",
		message: `${label} declares 'machineType' ('${safeRuleLabel(mt)}'), which is NOT SUPPORTED. The catalog-slug design it belongs to was never built — the field had no readers, so a fixture declaring it ran on the default pool regardless of what it said. Declare 'class' instead (one of: ${COMPUTE_CLASSES.join(", ")}) and put what you need in 'resources'.`
	};
}
/**
* node-pool-demand-provisioning WS-6 / D11 — GPU folds into the machine type. The `gpu` block is
* REMOVED from the contract for NEW publishes (fail loud); the field is retained in type+schema so
* already-published manifests still validate at the operator/CP (back-compat, the D22 redis/
* endpoints.public pattern).
*
* ⭐ R-9: the `rule` id is BYTE-IDENTICAL to what shipped (`fixture-gpu-use-node-type`) and only the
* MESSAGE now names `machineType`. The `rule` field exists so callers can suppress/dedupe — it is a
* gate name, not prose. Rename what a human reads; never what a gate names.
*/
function gpuUseNodeTypeIssue(path, label) {
	return {
		severity: "error",
		path,
		rule: "fixture-gpu-use-node-type",
		message: `${label} declares a 'gpu' block, which is REMOVED from the contract. GPUs come from the COMPUTE CLASS: declare 'class: gpu-inference' for serving (the platform picks the accelerator, or pin one with 'accelerator'), or 'class: gpu-training' with a 'gpuCount' for multi-GPU training. The accelerator limit and toleration derive from the resolved machine type. Remove the 'gpu' block.`
	};
}
/** Prefer a declared name for a message; fall back to the JSON pointer when the name is absent (the
*  manifest is `unknown` — these rules run even when schema validation fails, so a name may be
*  missing or non-string). */
function nameOrPath(name, fallbackPath) {
	return typeof name === "string" ? safeRuleLabel(name) : fallbackPath;
}
/** Longest untrusted fragment {@link safeRuleLabel} will echo into a rule message. Mirrors
*  `compute-class.ts`'s `DETAIL_LABEL_MAX` deliberately — see that function's docblock. */
const RULE_LABEL_MAX = 64;
/**
* ⛔ THE M-1 DEFENCE — render an UNTRUSTED, APP-AUTHORED value for a rule message.
*
* Every string this module interpolates (`nodeType`, `machineType`, `sizing.mode`, and the unit
* NAME inside `label`) comes from a fixture manifest, and a `FixtureRuleIssue.message` reaches two
* sinks that make raw echo a real primitive:
*   1. **the CLI door** — `tools/zion-app`'s `validate/index.ts` runs the schema pass and these
*      rules INDEPENDENTLY, so a manifest whose Ajv pass already FAILED still reaches this message
*      and its bytes are printed to the author's terminal and into CI logs. NUL / CRLF / an ANSI CSI
*      / U+202E there is the `js/log-injection` + terminal-spoofing class;
*   2. **the CP door** — `bundle-parser` puts the whole message into `BundleFixtureManifestSchemaError`,
*      which the exception filter returns as an HTTP 400 body AND logs.
*
* ⚠️ MEASURED before this existed (security verify, 2026-08-08): an ANSI `nodeType` produced a
* 455-char message carrying a raw CSI, a CRLF one forged a `level=info msg="all clear"` log line, and
* an **Ajv-LEGAL 1 MB** `nodeType` produced a **1,000,443-char** message — multiplied by
* services × workers × fixtures per bundle.
*
* ⭐ WHY A LOCAL COPY rather than an import: `compute-class.ts`'s `safeLabel` is module-private, and
* exporting it would widen this package's public API with a utility for one internal caller. The two
* are deliberately IDENTICAL in behaviour — same printable-ASCII class, same 64-char bound, same
* `<typeof>` rendering for a non-string so a hostile `toString`/`Symbol.toPrimitive` cannot re-enter.
* A character-class replace is LINEAR, so this is ReDoS-free.
*/
function safeRuleLabel(v) {
	if (typeof v !== "string") return `<${typeof v}>`;
	const cleaned = v.replace(/[^\x20-\x7E]/g, "?");
	return cleaned.length > RULE_LABEL_MAX ? `${cleaned.slice(0, RULE_LABEL_MAX)}...` : cleaned;
}
/** A declared, non-empty string field, or `null` when the field is absent / empty / non-string.
*  Absent and empty-string are both the opt-out — the shipped behaviour of the retired
*  `checkOneNodeType` grammar check (compute-classes D3), preserved so a manifest carrying
*  `nodeType: ''` is still treated as not declaring one. */
function declaredString(v) {
	return typeof v === "string" && v !== "" ? v : null;
}
/**
* compute-classes CC-1/CC-2/CC-3 — the cross-field rules over one unit's class declaration.
*
* ⭐ EXPORTED because it is bound at more doors than this module owns:
*   1. the CLI publish door — `tools/zion-app` via `checkFixturePlacementAndResources`;
*   2. the CP publish door — `release-engine`'s `bundle-parser` via the same entry point;
*   3. the TYPED WORKER WIRE — `@zion/sdk-cp-dp-contracts`'s `WorkerDesiredStateSchema.superRefine`,
*      which is re-`.parse()`d at both CP write paths, so a worker that reaches CP by a route that
*      skips Ajv is still refused;
*   4. and Ajv mirrors it in `$defs/service.allOf` + `$defs/worker.allOf` (a SERVICE has no zod
*      schema — the Ajv file IS the manifest's door).
* Four bindings, one predicate. Writing the rule four times is how the four drift.
*
* Every access is guarded: at the CLI door these rules run EVEN WHEN SCHEMA VALIDATION FAILED, so
* `class` may be a number, `gpuCount` may be a string, and `sizing` may be a null.
*
* ⛔ The `class`-is-not-a-known-class arm is deliberately NOT here. Ajv's `enum` already refuses it
* with a precise message at all three doors, and the resolver refuses it typed at reconcile; a third
* report on the same byte is the double-reporting this module's `checkExtendedResources` docblock
* calls out as the ONE place double-reporting is justified — and this is not that place (Ajv's enum
* message is perfectly clear, unlike its `propertyName must match pattern`).
*/
function checkComputeClassDeclaration(unit, basePath, label) {
	const issues = [];
	const safeLabel = safeRuleLabel(label);
	const cls = unit.class;
	const isTraining = cls === "gpu-training";
	const isGpu = isComputeClass(cls) && isGpuComputeClass(cls);
	const classLabel = isComputeClass(cls) ? cls : "N";
	if (isTraining && unit.gpuCount === void 0) issues.push({
		severity: "error",
		path: `${basePath}/gpuCount`,
		rule: "gpu-count-required",
		message: `${safeLabel} declares class 'gpu-training' but no 'gpuCount'. Training machine types sell FIXED GPU-count shapes, so the count is what selects the shape — the cpu/memory vector is not used for this class. Declare 'gpuCount' (1-8).`
	});
	if (!isTraining && unit.gpuCount !== void 0) issues.push({
		severity: "error",
		path: `${basePath}/gpuCount`,
		rule: "gpu-count-not-allowed",
		message: `${safeLabel} declares 'gpuCount' on class '${classLabel}', where it means nothing. Only 'gpu-training' takes a GPU count; for 'gpu-inference' the accelerator count comes from the resolved machine type. Remove 'gpuCount' (or declare class 'gpu-training' if this is a multi-GPU training workload).`
	});
	if (unit.accelerator !== void 0 && !isGpu) issues.push({
		severity: "error",
		path: `${basePath}/accelerator`,
		rule: "accelerator-requires-gpu-class",
		message: `${safeLabel} declares 'accelerator' on class '${classLabel}', which has no GPUs. An accelerator pin is only honored on ${GPU_COMPUTE_CLASSES.join(" or ")}. Remove it, or declare the GPU class you meant.`
	});
	const sizing = unit.sizing;
	if (sizing !== null && typeof sizing === "object") {
		const sz = sizing;
		if (sz.size !== void 0 && sz.mode !== "fixed") issues.push({
			severity: "error",
			path: `${basePath}/sizing/size`,
			rule: "sizing-size-requires-fixed-mode",
			message: `${safeLabel} declares 'sizing.size' with mode '${sz.mode == null ? "<absent>" : safeRuleLabel(sz.mode)}'. Under 'tenant-scaled' the multiplier comes from the tenant, so a declared size would be ignored. Use mode 'fixed' with this size, or drop the size.`
		});
	}
	return issues;
}
/**
* compute-classes D29 (PA-Docs E3, Arm B) — `sizing` requires a declared base vector.
*
* ⛔ WHY AN ERROR RATHER THAN A DEFAULT. `sizing` scales the unit's DECLARED `resources`. With none
* declared the unit gets `PLATFORM_DEFAULT_CONTAINER_RESOURCES` — a PLATFORM constant — and
* multiplying that by a TENANT's multiplier makes the platform's own baseline tenant-dependent, which
* nobody decided and no screen would explain. Ignoring the field instead would make it the ninth
* validated-then-ignored manifest field, which is the defect class this program exists to end.
*
* ⚠️ ACCEPTS A MULTI-CONTAINER SERVICE WHOSE CONTAINERS DECLARE RESOURCES (D29's E5): the effective
* vector of such a unit is the SUM of its app-declared containers' requests, so the base exists even
* though `service.resources` is absent — and a service-level `resources` beside `containers[]` is
* already a separate error (`resources-on-multi-container-service`). Checking only the service level
* would refuse every correctly-authored multi-container fixture that wants tenant scaling.
*
* ⚠️ `sizing.mode: 'fixed'` with no explicit `size` is `×1`, which changes nothing — but it is STILL
* refused, deliberately: the declaration says "scale me" and the manifest gives nothing to scale, so
* accepting it teaches the author a false model that breaks the day they set `size: '2x'`. Fail at the
* cheap moment.
*
* ⛔ NO Ajv TWIN, and that is deliberate. JSON Schema can express "if `sizing` then require
* `resources`" on a service — but not the multi-container arm, which needs "…or any element of
* `containers` declares `resources`". `contains` could approximate it, and an approximation that
* refuses a legal manifest at the operator's RECONCILE-time Ajv door would stop a published fixture
* from deploying. ⇒ semantic rule only, at the two PUBLISH doors, exactly as
* `resources-requests-exceed-limits` already is.
*/
function checkSizingHasBase(unit, basePath, label) {
	if (unit.sizing === void 0) return [];
	if (unit.resources !== void 0) return [];
	if ((Array.isArray(unit.containers) ? unit.containers : []).some((c) => c !== null && typeof c === "object" && c.resources !== void 0)) return [];
	return [{
		severity: "error",
		path: `${basePath}/sizing`,
		rule: "sizing-requires-resources",
		message: `${label} declares 'sizing' but no 'resources' to scale. Sizing multiplies your DECLARED resource vector, and with none declared this unit would run on the platform default (250m CPU / 256Mi memory) — multiplying a platform default by a tenant's size is not something the platform will do silently. Declare 'resources' calibrated at tenant size '1x' (on the containers, for a multi-container service), or remove 'sizing'.`
	}];
}
/**
* compute-classes D3 — placement rules for ONE unit that can carry `nodeType` / `machineType` /
* `class` / `gpuCount` / `accelerator`.
*
* ⚠️ THE RULE IDS `node-type-malformed`, `node-type-unknown-family`, `machine-type-malformed` and
* `machine-type-conflicts-with-node-type` ARE RETIRED and must not be re-introduced. They validated
* the SHAPE of two fields that are now refused outright, so they can only fire on a manifest that
* already has a louder error. Recorded here rather than deleted silently because a rule id is a
* GATE NAME — a caller may still be suppressing one, and a reader who greps for it deserves to find
* out what replaced it (`node-type-removed` / `machine-type-not-supported`).
*/
function checkUnitPlacement(unit, basePath, label) {
	const issues = [];
	const nt = declaredString(unit.nodeType);
	const mt = declaredString(unit.machineType);
	if (nt !== null) issues.push(nodeTypeRemovedIssue(`${basePath}/nodeType`, label, nt));
	if (mt !== null) issues.push(machineTypeNotSupportedIssue(`${basePath}/machineType`, label, mt));
	issues.push(...checkComputeClassDeclaration(unit, basePath, label));
	return issues;
}
/**
* The two rules over a service's `topology.maxReplicas`. Both are errors, and each fires only when
* the members it compares are numbers; a non-number is the schema's refusal to report. JSON Schema
* cannot compare two numeric properties and no Ajv door sets `$data`, so these rules are code, in
* the family both doors run (D3 of `2026-09-23-census-bump-and-topology-ceiling`).
*
* `topology-max-replicas-below-replicas`: `topology.maxReplicas` must be at least the floor, which
* is the higher of `topology.replicas` and, when `scaling.algorithm` names a custom autoscaler,
* `scaling.minReplicas`, among those declared (as ruled on architecture-2 and Copilot finding 2 on
* PR #3084). The operator does not honour a smaller ceiling: `resolveHsoMaxReplicas` and
* `resolveCustomClamp` raise a ceiling below the floor to the floor (KEDA rejects `max < min`), so
* `{ replicas: 3, maxReplicas: 2 }` publishes a ceiling of 2 and runs 3 pods, and on the
* custom-scaler path `scaling.minReplicas: 3` does the same to `topology.maxReplicas: 2`. The
* default HTTP scaler ignores `scaling.minReplicas`, so with `scaling.algorithm` absent or
* `'default'` that member is no floor and `{ maxReplicas: 1 }` beside `scaling.minReplicas: 3`
* passes. The rule reads only the manifest, so any other string counts as a custom autoscaler here,
* including a name the operator cannot resolve; the operator's fallback to HTTP for an unresolved
* name is its own concern. The message names the floor it failed against and both values; on a tie
* it names `topology.replicas`. With neither floor member in play the rule is silent: a missing
* `replicas` means one pod, which every legal `maxReplicas` (`minimum: 1`) already covers.
*
* `topology-max-replicas-above-scaling`: `topology.maxReplicas` must not exceed the deprecated
* `scaling.maxReplicas` when both are declared (as ruled on security-1). The operator takes the lower
* of the two, so a manifest declaring both says two ceilings and runs the smaller one; refusing it
* keeps the new key from reading as a raise of a ceiling a reviewer approved. Equal values pass.
*
* Pure. Returns at most one issue per rule, the floor issue first.
*/
function checkTopologyMaxReplicas(topology, scaling, basePath, label) {
	if (topology === null || typeof topology !== "object") return [];
	const { replicas, maxReplicas } = topology;
	if (typeof maxReplicas !== "number") return [];
	const { algorithm, minReplicas: scalingMin, maxReplicas: scalingMax } = scaling !== null && typeof scaling === "object" ? scaling : {};
	const customScaler = typeof algorithm === "string" && algorithm !== "default";
	const issues = [];
	const floors = [];
	if (typeof replicas === "number") floors.push({
		field: "topology.replicas",
		lower: "replicas",
		value: replicas
	});
	if (customScaler && typeof scalingMin === "number") floors.push({
		field: "scaling.minReplicas",
		lower: "scaling.minReplicas",
		value: scalingMin
	});
	const floor = floors.reduce((best, f) => best === void 0 || f.value > best.value ? f : best, void 0);
	if (floor !== void 0 && maxReplicas < floor.value) issues.push({
		severity: "error",
		path: `${basePath}/topology/maxReplicas`,
		rule: "topology-max-replicas-below-replicas",
		message: floor.field === "scaling.minReplicas" ? `${label} declares topology.maxReplicas ${maxReplicas} below scaling.minReplicas ${floor.value}. On a custom-scaler path the clamp floor is scaling.minReplicas, so the operator would raise the ceiling to ${floor.value}; the default HTTP scaler ignores scaling.minReplicas. Set maxReplicas to at least ${floor.value}, or lower scaling.minReplicas.` : `${label} declares topology.maxReplicas ${maxReplicas} below ${floor.field} ${floor.value}. The operator never runs fewer than ${floor.field} pods, so it would raise the ceiling to ${floor.value}. Set maxReplicas to at least ${floor.value}, or lower ${floor.lower}.`
	});
	if (typeof scalingMax === "number" && maxReplicas > scalingMax) issues.push({
		severity: "error",
		path: `${basePath}/topology/maxReplicas`,
		rule: "topology-max-replicas-above-scaling",
		message: `${label} declares topology.maxReplicas ${maxReplicas} above scaling.maxReplicas ${scalingMax}. The operator runs the lower of the two ceilings, so it would stop at ${scalingMax}. Set topology.maxReplicas to at most ${scalingMax}, or raise scaling.maxReplicas to ${maxReplicas} if the higher ceiling is intended.`
	});
	return issues;
}
/** The two sides of a `resources` block, in the order a message should name them. */
const RESOURCE_SIDES = ["requests", "limits"];
/**
* R-9 F-2 — a friendly refusal for EXTENDED resources.
*
* The schema's `propertyNames` already rejects `nvidia.com/gpu`, but its Ajv message
* (`propertyName must match pattern`) does not say WHY. ⚠️ This is the ONE place this rule set
* deliberately double-reports with the schema, and the reason is that the schema's message is actively
* unhelpful on the exact mistake an author is most likely to make.
*
* Fires for any key containing `/` or `.` — the vendor-domain shape.
*/
function checkExtendedResources(resources, basePath) {
	if (resources === null || typeof resources !== "object") return [];
	const issues = [];
	for (const side of RESOURCE_SIDES) {
		const block = resources[side];
		if (block === null || typeof block !== "object") continue;
		for (const key of Object.keys(block)) {
			if (!key.includes("/") && !key.includes(".")) continue;
			issues.push({
				severity: "error",
				path: `${basePath}/resources/${side}/${key}`,
				rule: "resources-extended-resource-refused",
				message: `resources.${side}.${safeRuleLabel(key)} names an EXTENDED resource, which the platform refuses. A GPU comes from the COMPUTE CLASS: declare 'class: gpu-inference' for serving, or 'class: gpu-training' with a 'gpuCount' for multi-GPU training, and the platform stamps the accelerator limit from the resolved machine type. Requesting an accelerator here would let a fixture ask for a GPU on a machine type that has none — scheduled never, explained nowhere.`
			});
		}
	}
	return issues;
}
/**
* R-9 F-6 — a request above its limit is a pod spec Kubernetes rejects, and the rejection surfaces as
* an un-schedulable workload with nothing on screen naming the manifest. Caught here instead.
*
* ⛔ SKIPS silently when either side is unparseable (`compareScaledK8sQuantities` → null, the
* inputs `parseK8sQuantity` refuses). The JSON schema's quantity `pattern` already reports a
* malformed quantity, and at the CLI door these rules run EVEN
* WHEN SCHEMA VALIDATION FAILED — emitting a second, vaguer diagnostic on the same byte is noise.
*
* ⚠️ The two doors reach that arm differently, which matters for coverage: at the CP door Ajv has
* ALREADY passed, so an unparseable quantity is impossible and the arm is unreachable; at the CLI door
* it is reachable and is tested there. Same function, two callers, one reachable branch each — do not
* "simplify" the arm away because the CP tests never hit it.
*/
function checkResourceBounds(resources, basePath) {
	if (resources === null || typeof resources !== "object") return [];
	const req = resources.requests;
	const lim = resources.limits;
	if (req === null || typeof req !== "object") return [];
	if (lim === null || typeof lim !== "object") return [];
	const issues = [];
	for (const key of Object.keys(req)) {
		const order = compareScaledK8sQuantities(req[key], 1, lim[key], 1);
		if (order === null) continue;
		if (order > 0) issues.push({
			severity: "error",
			path: `${basePath}/resources/requests/${key}`,
			rule: "resources-requests-exceed-limits",
			message: `resources.requests.${safeRuleLabel(key)} ('${safeRuleLabel(req[key])}') exceeds resources.limits.${safeRuleLabel(key)} ('${safeRuleLabel(lim[key])}'). Kubernetes rejects a container whose request is above its limit; the pod would never be admitted. Lower the request or raise the limit.`
		});
	}
	return issues;
}
/** Both resource rules over one unit's `resources` block. */
function checkResources(resources, basePath) {
	return [...checkExtendedResources(resources, basePath), ...checkResourceBounds(resources, basePath)];
}
/**
* The ONE entry point both doors call. `manifest` is `unknown`: at the CLI these rules run even when
* schema validation FAILED, so every access is guarded. Pure.
*
* Rules emitted here (all `error`; the deprecation WARNINGS live in `@zion/sdk-node`'s
* `manifest-rules.ts` and are CLI-only, because the publish response has no diagnostics channel):
*   - `node-type-removed`, `machine-type-not-supported`                      (compute-classes D3)
*   - `gpu-count-required`, `gpu-count-not-allowed`,
*     `accelerator-requires-gpu-class`, `sizing-size-requires-fixed-mode`    (compute-classes CC-1..3)
*   - `sizing-requires-resources`                                            (compute-classes D29 E3)
*   - `resources-on-multi-container-service`, `resources-requests-exceed-limits`,
*     `resources-extended-resource-refused`
*   - `fixture-resources-and-infrastructure`
*   - `fixture-gpu-use-node-type`
*   - `topology-max-replicas-below-replicas`, `topology-max-replicas-above-scaling`
*   - the same class/sizing/resource family over `pools[]`                    (WS-2)
*
* ⚠️ RETIRED rule ids — do not re-introduce: `node-type-malformed`,
* `node-type-unknown-family`, `machine-type-malformed`,
* `machine-type-conflicts-with-node-type`. They validated the SHAPE of two fields that are now
* refused outright (compute-classes D3), so they can only fire on a manifest that already carries a
* louder error. A rule id is a GATE NAME, so the retirement is written down rather than done
* silently.
*/
function checkFixturePlacementAndResources(manifest) {
	if (manifest === null || typeof manifest !== "object") return [];
	const m = manifest;
	const issues = [];
	const services = Array.isArray(m.services) ? m.services : [];
	for (let si = 0; si < services.length; si++) {
		const svc = services[si];
		if (svc === null || typeof svc !== "object") continue;
		const s = svc;
		const svcPath = `/services/${si}`;
		const svcLabel = `service '${nameOrPath(s.name, svcPath)}'`;
		issues.push(...checkUnitPlacement(s, svcPath, svcLabel));
		issues.push(...checkTopologyMaxReplicas(s.topology, s.scaling, svcPath, svcLabel));
		issues.push(...checkSizingHasBase(s, svcPath, svcLabel));
		if (s.gpu !== void 0) issues.push(gpuUseNodeTypeIssue(`${svcPath}/gpu`, svcLabel));
		const containers = Array.isArray(s.containers) ? s.containers : [];
		if (containers.length > 0 && s.resources !== void 0) issues.push({
			severity: "error",
			path: `${svcPath}/resources`,
			rule: "resources-on-multi-container-service",
			message: `service '${nameOrPath(s.name, svcPath)}' declares both 'containers[]' and a service-level 'resources' block. In Kubernetes 'resources' is a CONTAINER-level field, so on a multi-container pod a service-level block names no container. Move it to the container(s) it describes: services[i].containers[j].resources.`
		});
		else issues.push(...checkResources(s.resources, svcPath));
		for (let ci = 0; ci < containers.length; ci++) {
			const c = containers[ci];
			if (c === null || typeof c !== "object") continue;
			const cl = c;
			const cPath = `${svcPath}/containers/${ci}`;
			if (cl.gpu !== void 0) issues.push(gpuUseNodeTypeIssue(`${cPath}/gpu`, `${svcLabel} container '${nameOrPath(cl.name, cPath)}'`));
			issues.push(...checkResources(cl.resources, cPath));
		}
	}
	const workers = Array.isArray(m.workers) ? m.workers : [];
	for (let wi = 0; wi < workers.length; wi++) {
		const w = workers[wi];
		if (w === null || typeof w !== "object") continue;
		const wl = w;
		const wPath = `/workers/${wi}`;
		issues.push(...checkUnitPlacement(wl, wPath, `worker '${nameOrPath(wl.name, wPath)}'`));
		issues.push(...checkSizingHasBase(wl, wPath, `worker '${nameOrPath(wl.name, wPath)}'`));
		issues.push(...checkResources(wl.resources, wPath));
	}
	const pools = Array.isArray(m.pools) ? m.pools : [];
	for (let pi = 0; pi < pools.length; pi++) {
		const p = pools[pi];
		if (p === null || typeof p !== "object") continue;
		const pl = p;
		const pPath = `/pools/${pi}`;
		const pLabel = `pool '${nameOrPath(pl.name, pPath)}'`;
		issues.push(...checkComputeClassDeclaration(pl, pPath, pLabel));
		issues.push(...checkSizingHasBase(pl, pPath, pLabel));
		issues.push(...checkResources(pl.resources, pPath));
	}
	const jobs = Array.isArray(m.jobs) ? m.jobs : [];
	for (let ji = 0; ji < jobs.length; ji++) {
		const j = jobs[ji];
		if (j === null || typeof j !== "object") continue;
		issues.push(...checkResources(j.resources, `/jobs/${ji}`));
	}
	if (m.infrastructure !== void 0 && m.resources !== void 0) issues.push({
		severity: "error",
		path: "/infrastructure",
		rule: "fixture-resources-and-infrastructure",
		message: "this fixture declares BOTH the root 'infrastructure' block and its deprecated alias 'resources'. They are two names for one block, and merging them would be a silent way to lose a key. Keep 'infrastructure' and remove 'resources'."
	});
	return issues;
}
//#endregion
export { checkComputeClassDeclaration, checkFixturePlacementAndResources, safeRuleLabel };

//# sourceMappingURL=fixture-placement-rules.js.map